import { InfiniteData } from '@tanstack/react-query';
import { isEmpty, orderBy, toNumber, toString, toUpper } from 'lodash';
import { SortOptions } from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { DefaultQueryProps } from 'src/hooks';
import { IProduct } from 'src/interfaces';
import { naturalSort } from 'src/utils';

import { filterProductByKeywords } from '../../../listProductsByCategoryPage/modules/ListProductsByCategoryPage.helper';

const formatSortName = (sort: SortOptions) => {
  switch (sort) {
    case SortOptions.DATE_CREATED:
      return 'created_at';
    case SortOptions.LAST_MODIFIED:
      return 'modified_at';
    default:
      return [(product: IProduct) => product.product_name?.toLowerCase()];
  }
};

export const hoistExactMatches = <T = unknown>(
  array: T[],
  values: string[],
  fieldName: keyof T,
) => {
  if (isEmpty(values)) return array;

  const newArray = array.reduce((acc, item) => {
    if (
      !!values.find(
        (ele) => toUpper(ele) === toUpper(toString(item[fieldName])),
      )
    ) {
      return [item, ...acc];
    }

    return [...acc, item];
  }, [] as T[]);

  return newArray;
};

export const filteredProducts = (
  products: IProduct[],
  activeFilters: DefaultQueryProps,
  sort: SortOptions,
  order: 'desc' | 'asc',
) => {
  let filteredProducts = [...products];

  if ('name.regexp' in activeFilters) {
    filteredProducts = products.filter((product) => {
      if ('name.regexp' in activeFilters) {
        const values = activeFilters['name.regexp'];

        return filterProductByKeywords(values, product);
      }

      return false;
    });
  }

  if ('product_category_id.list' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      const values = activeFilters['product_category_id.list'];

      return values.some((value) =>
        product.product_category_id.includes(toNumber(value)),
      );
    });
  }

  if ('status.list' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      if ('status.list' in activeFilters) {
        const values = activeFilters['status.list'];

        return values.some(
          (value) => Boolean(toNumber(value)) === product.active,
        );
      }

      return false;
    });
  }

  if ('product_variation_size_id.list' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      if ('product_variation_size_id.list' in activeFilters) {
        const values = activeFilters['product_variation_size_id.list'];

        return values.some((value) =>
          product.productVariations.some(
            (item) => item.product_variation_size_id === toNumber(value),
          ),
        );
      }

      return false;
    });
  }

  let sortedProducts =
    sort === SortOptions.ALPHABETICALLY
      ? hoistExactMatches(
          naturalSort(filteredProducts, 'product_name', order),
          activeFilters['name.regexp'],
          'product_name',
        )
      : orderBy(filteredProducts, formatSortName(sort), order);

  return sortedProducts;
};

export const formatProductGridData = (
  infiniteProductsData?: InfiniteData<{
    products: IProduct[];
    totalPages: any;
    totalCount: any;
  }>,
): IProduct[] => {
  if (!infiniteProductsData || !Array.isArray(infiniteProductsData.pages)) {
    return [];
  }

  return (
    infiniteProductsData?.pages?.flatMap((page) =>
      Array.isArray(page.products) ? page.products : [],
    ) ?? []
  );
};
